/**
 * Libellé d'une donnée commune AKONGO dans la langue de l'utilisateur.
 *
 * Règle produit (décision du 03/11/2026) : l'anglais est la langue minimale
 * d'une donnée commune. Quand la langue demandée manque, on affiche
 * l'anglais, puis le français, puis n'importe quelle autre langue renseignée,
 * puis le libellé brut fourni par l'appelant. Jamais une case vide quand un
 * texte existe dans une langue.
 *
 * Même ordre sur le mobile (WellfareMonitor2) et dans l'API (api_akongo).
 * ADR : docs/decisions/2026-11-03-anglais-langue-minimale-donnees-communes.md
 */

export const DATA_LANGUAGES = Object.freeze(["fr", "en", "de", "nl", "es"]);
export const DATA_FALLBACK_LANGUAGES = Object.freeze(["en", "fr"]);
/** Langue qu'une donnée commune doit toujours avoir. */
export const REQUIRED_DATA_LANGUAGE = "en";

const clean = (value) => (typeof value === "string" ? value.trim() : "");

/** « nl-NL », « NL », « nl_BE » → « nl » ; valeur inutilisable → « en ». */
export const dataLanguage = (language) => {
  const base = String(language || "").split(/[-_]/)[0].toLowerCase();
  return /^[a-z]{2,3}$/.test(base) ? base : "en";
};

/** Ordre de lecture : langue demandée, anglais, français (sans doublon). */
export const dataLanguageOrder = (language) => Array.from(new Set([dataLanguage(language), ...DATA_FALLBACK_LANGUAGES]));

/**
 * Texte d'un objet multilingue `{ fr, en, de, nl, es, … }`.
 * Une chaîne simple est rendue telle quelle (donnée non traduite).
 * @param {object|string|null} value
 * @param {string} language langue de l'utilisateur (« fr », « de-DE »…)
 * @param {string} [fallback] libellé brut si aucune langue n'est renseignée
 * @returns {string} le texte, ou `fallback` (chaîne vide par défaut)
 */
export const pickLocalized = (value, language, fallback = "") => {
  if (typeof value === "string") return clean(value) || clean(fallback);
  if (value && typeof value === "object") {
    for (const key of dataLanguageOrder(language)) {
      const text = clean(value[key]);
      if (text) return text;
    }
    for (const key of [...DATA_LANGUAGES, ...Object.keys(value)]) {
      const text = clean(value[key]);
      if (text) return text;
    }
  }
  return clean(fallback);
};

/** Vrai si l'objet multilingue a un texte anglais (langue minimale). */
export const hasRequiredLanguage = (value) => Boolean(value && typeof value === "object" && clean(value[REQUIRED_DATA_LANGUAGE]));

/**
 * Nom commun d'une espèce (ou d'un animal qui en porte la copie). Le
 * catalogue n'a que le français (`specieName`) et l'anglais
 * (`specieEnglishName`) : les autres langues lisent l'anglais d'abord.
 * Sans nom commun, le nom latin.
 */
export const speciesCommonName = (species, language) => {
  if (!species || typeof species !== "object") return "";
  return pickLocalized(
    { fr: species.specieName, en: species.specieEnglishName },
    language,
    species.specieLatinName,
  );
};
